import { useEffect, useState } from 'react'

import type { TellerSession, Till } from '@/api/teller-types'
import { MoneyDisplay } from '@/components/money-display'
import { MoneyInput } from '@/components/money-input'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Spinner } from '@/components/ui/spinner'
import type { Pesewas } from '@/lib/money'
import { cn } from '@/lib/utils'

import { countPurposeLabel, tillTypeLabel } from './constants'
import { DualControlNotice, DualControlOutcome } from './dual-control'
import { useCloseTellerSession, useOpenTellerSession } from './mutations'
import { TELLER_PERMISSIONS } from './permissions'
import { TellerActionDialog } from './teller-action-dialog'

/** A note is capped at 500 characters by the server (`teller.note_too_long`). */
const NOTE_LIMIT = 500

/**
 * Opening a shift.
 *
 * Only counter tills are offered, and only ones that are active and not already open. That mirrors
 * three refusals rather than replacing them: a field till answers
 * `teller.field_till_needs_no_session`, a suspended drawer answers `teller.till_not_active`, and a
 * drawer already worked by somebody — or a teller who already holds a session anywhere — answers
 * `teller.session_already_open`. The last of those cannot be filtered out here, because this screen
 * cannot see whether the user has a session open on another branch's drawer.
 */
export function OpenSessionDialog({
  tills,
  isLoadingTills,
  open,
  onOpenChange,
  onOpened,
}: {
  tills: Till[]
  isLoadingTills: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  onOpened?: (session: TellerSession) => void
}) {
  const [selected, setSelected] = useState<Till | null>(null)
  const openSession = useOpenTellerSession()

  useEffect(() => {
    if (!open) {
      setSelected(null)
      openSession.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const candidates = tills.filter(
    (till) => till.tillType === 'Counter' && till.status === 'Active' && till.openSessionId === null,
  )

  return (
    <TellerActionDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Open a till"
      description="A shift starts with the cash already in the drawer, which becomes the opening float."
      confirmLabel="Open the till"
      isPending={openSession.isPending}
      disabled={selected === null}
      error={openSession.isError ? openSession.error : null}
      onConfirm={() => {
        if (!selected) {
          return
        }

        openSession.mutate(
          { tillId: selected.id },
          {
            onSuccess: (session) => {
              onOpened?.(session)
              onOpenChange(false)
            },
          },
        )
      }}
    >
      <ul className="max-h-64 divide-y divide-border-subtle overflow-y-auto rounded-card border border-border-subtle">
        {isLoadingTills ? (
          <li className="flex items-center gap-2 p-3 text-xs text-muted">
            <Spinner className="size-4" />
            Reading the branch&apos;s drawers…
          </li>
        ) : candidates.length === 0 ? (
          <li className="p-3 text-xs text-muted">
            No counter till at this branch is free. A drawer already carrying an open session cannot
            take a second one, and a field till never takes one at all.
          </li>
        ) : (
          candidates.map((till) => {
            const chosen = selected?.id === till.id
            return (
              <li key={till.id}>
                <button
                  type="button"
                  onClick={() => setSelected(till)}
                  aria-pressed={chosen}
                  className={cn(
                    'flex min-h-11 w-full items-center gap-3 px-3 py-2 text-left transition-colors',
                    chosen ? 'bg-brand-fill-subtle' : 'hover:bg-surface-hover',
                  )}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-heading">
                      {till.tillNumber} · {till.name}
                    </span>
                    <span className="block text-2xs text-muted">{tillTypeLabel(till.tillType)}</span>
                  </span>
                  <MoneyDisplay value={till.cashOnHandMinor} currency={till.currencyCode} />
                </button>
              </li>
            )
          })
        )}
      </ul>

      <p className="text-xs text-muted">
        The opening float is whatever the drawer holds when the shift starts — it is read from the
        till, not keyed. Count it before you trade: an opening count is the only evidence of what you
        were handed.
      </p>
    </TellerActionDialog>
  )
}

/**
 * Declaring the drawer and closing the shift.
 *
 * Three things about this are said on the dialog rather than discovered afterwards.
 *
 * The declaration must equal the closing count where one exists, or the server refuses the whole
 * close with `teller.declaration_disagrees_with_count`. So the field is pre-filled from the latest
 * count and a divergence is called out before the button is pressed.
 *
 * Confirming does not close the shift. It persists the declaration, moves the session to
 * `PendingClose` — which stops the drawer trading — and queues a countersignature.
 *
 * And the countersignature is always somebody else: `teller.session.open` and
 * `teller.eod.submit.approve` are an incompatible pair in the segregation matrix, so a teller can
 * never hold both halves. There is no "you may be able to complete this yourself" case here.
 */
export function CloseSessionDialog({
  session,
  open,
  onOpenChange,
}: {
  session: TellerSession
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const closingCount =
    session.latestCount && session.latestCount.purpose === 'Closing' ? session.latestCount : null

  const [declared, setDeclared] = useState<Pesewas | null>(null)
  const [note, setNote] = useState('')
  const close = useCloseTellerSession(session.id)

  useEffect(() => {
    if (open) {
      // Pre-filled from the count, because those are the two figures the server insists agree.
      setDeclared(closingCount ? closingCount.totalMinor : null)
      setNote('')
    } else {
      close.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, closingCount?.id])

  const overShort = declared === null ? null : declared - session.expectedClosingCashMinor
  const disagreesWithCount = closingCount !== null && declared !== null && declared !== closingCount.totalMinor

  return (
    <TellerActionDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Declare and close the till"
      description={`Till ${session.tillNumber}, ${session.tellerUsername}. The drawer stops trading as soon as this is submitted.`}
      confirmLabel="Submit the declaration"
      isPending={close.isPending}
      disabled={declared === null || note.length > NOTE_LIMIT}
      error={close.isError ? close.error : null}
      notice={
        <DualControlNotice
          action="Closing a till"
          approvePermission={TELLER_PERMISSIONS.eodSubmitApprove}
          canCountersign={false}
          barredPair
        />
      }
      outcome={
        close.data ? (
          <DualControlOutcome
            result={close.data}
            awaitingDescription="The declaration is recorded and the drawer has stopped trading. The shift closes, and any over or short posts to the ledger, when a supervisor countersigns."
            appliedDescription="The shift is closed and any over or short has posted to the ledger."
            deferredWarning={
              <span className="text-xs">
                If the count is rejected, record a fresh count — it supersedes this declaration
                rather than leaving you unable to close.
              </span>
            }
          />
        ) : undefined
      }
      onConfirm={() => {
        if (declared === null) {
          return
        }

        close.mutate({
          declaredClosingCashMinor: declared,
          note: note.trim() === '' ? null : note.trim(),
        })
      }}
    >
      <div className="grid gap-3 rounded-card border border-border-subtle bg-surface-muted p-3 sm:grid-cols-3">
        <div>
          <p className="text-2xs font-semibold tracking-wide text-muted uppercase">Expected</p>
          <MoneyDisplay
            value={session.expectedClosingCashMinor}
            currency={session.currencyCode}
            className="text-left"
          />
        </div>
        <div>
          <p className="text-2xs font-semibold tracking-wide text-muted uppercase">Latest count</p>
          <MoneyDisplay
            value={closingCount ? closingCount.totalMinor : null}
            currency={session.currencyCode}
            className="text-left"
          />
          <p className="text-2xs text-muted">
            {closingCount
              ? countPurposeLabel(closingCount.purpose)
              : session.latestCount
                ? `Only a ${countPurposeLabel(session.latestCount.purpose).toLowerCase()} exists.`
                : 'No count has been recorded.'}
          </p>
        </div>
        <div>
          <p className="text-2xs font-semibold tracking-wide text-muted uppercase">Over or short</p>
          <MoneyDisplay
            value={overShort}
            currency={session.currencyCode}
            signed
            colourNegative
            className="text-left"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="declared-cash">Declared closing cash</Label>
        <MoneyInput
          id="declared-cash"
          value={declared}
          onChange={setDeclared}
          currency={session.currencyCode}
        />
        <p className="text-xs text-muted">
          Zero is a legitimate declaration — a drawer banked to the vault before close holds nothing.
        </p>
      </div>

      {disagreesWithCount ? (
        <Alert tone="danger">
          <div className="space-y-1">
            <AlertTitle>This does not match the closing count</AlertTitle>
            <AlertDescription>
              The count sheet adds up to{' '}
              <MoneyDisplay
                value={closingCount.totalMinor}
                currency={session.currencyCode}
                className="inline-block text-left"
              />
              . The server refuses a declaration that disagrees with it —{' '}
              <span className="font-mono text-xs">teller.declaration_disagrees_with_count</span>.
              Recount the drawer, or correct the figure to match the sheet.
            </AlertDescription>
          </div>
        </Alert>
      ) : null}

      <div className="space-y-1.5">
        <Label htmlFor="close-note">Note</Label>
        <Input
          id="close-note"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          autoComplete="off"
          placeholder="What explains a difference, if there is one"
          aria-describedby="close-note-help"
        />
        <p id="close-note-help" className="text-xs text-muted">
          Optional, up to {NOTE_LIMIT} characters. It is read by the supervisor countersigning, and
          it is the only place a difference gets explained in the teller&apos;s own words.
        </p>
      </div>
    </TellerActionDialog>
  )
}
